import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { VideoWithTags, Tag } from '../types/video';
import { Search, Star, Play, Clock, X, ArrowLeft } from 'lucide-react';

export default function VideoLibrary() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [videos, setVideos] = useState<VideoWithTags[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<VideoWithTags[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [continueWatching, setContinueWatching] = useState<any[]>([]);
  const [completedVideos, setCompletedVideos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  useEffect(() => {
    loadData();
    const filter = searchParams.get('filter') || 'all';
    setCategoryFilter(filter);

    const tagParam = searchParams.get('tag');
    if (tagParam) {
      setSelectedTags([tagParam]);
    }
  }, []);

  useEffect(() => {
    filterVideos();
  }, [videos, selectedTags, searchQuery, categoryFilter, favorites, continueWatching, completedVideos]);

  async function loadData() {
    try {
      const [videosData, tagsData, favoritesData, progressData, completedData] = await Promise.all([
        loadVideos(),
        loadTags(),
        loadFavorites(),
        loadProgress(),
        loadCompleted(),
      ]);

      setVideos(videosData);
      setAllTags(tagsData);
      setFavorites(new Set(favoritesData));
      setContinueWatching(progressData);
      setCompletedVideos(new Set(completedData));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadVideos() {
    const { data: videosData, error: videosError } = await supabase
      .from('videos')
      .select('*')
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });

    if (videosError) throw videosError;

    const videosWithTags = await Promise.all(
      (videosData || []).map(async (video) => {
        const { data: tagData } = await supabase
          .from('video_tags')
          .select('tag_id, tags(*)')
          .eq('video_id', video.id);

        return {
          ...video,
          tags: tagData?.map((vt: any) => vt.tags) || [],
        };
      })
    );

    return videosWithTags;
  }

  async function loadTags() {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .order('name');

    if (error) throw error;
    return data || [];
  }

  async function loadFavorites() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_favorites')
      .select('video_id')
      .eq('user_id', user.id);

    if (error) throw error;
    return data?.map((f) => f.video_id) || [];
  }

  async function loadProgress() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_video_progress')
      .select('video_id, progress_seconds, completed')
      .eq('user_id', user.id)
      .eq('completed', false)
      .gt('progress_seconds', 0)
      .order('last_watched_at', { ascending: false })
      .limit(3);

    if (error) throw error;
    return data || [];
  }

  async function loadCompleted() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('user_video_progress')
      .select('video_id')
      .eq('user_id', user.id)
      .eq('completed', true);

    if (error) throw error;
    return data?.map((p) => p.video_id) || [];
  }

  function filterVideos() {
    let filtered = videos;

    if (categoryFilter === 'favorites') {
      filtered = filtered.filter((video) => favorites.has(video.id));
    } else if (categoryFilter === 'completed') {
      filtered = filtered.filter((video) => completedVideos.has(video.id));
    } else if (categoryFilter === 'in-progress') {
      const inProgressIds = new Set(continueWatching.map((p) => p.video_id));
      filtered = filtered.filter((video) => inProgressIds.has(video.id));
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter((video) =>
        video.tags?.some((tag) => selectedTags.includes(tag.id))
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (video) =>
          video.title.toLowerCase().includes(query) ||
          video.description.toLowerCase().includes(query)
      );
    }

    setFilteredVideos(filtered);
  }

  function toggleTag(tagId: string) {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  }

  function clearFilters() {
    setSelectedTags([]);
    setSearchQuery('');
  }

  function formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function getTagCount(tagId: string): number {
    return videos.filter((video) => video.tags?.some((tag) => tag.id === tagId)).length;
  }

  const featuredVideos = videos.filter((v) => v.is_featured).slice(0, 3);
  const continueWatchingVideos = continueWatching
    .map((progress) => videos.find((v) => v.id === progress.video_id))
    .filter(Boolean) as VideoWithTags[];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading video library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/dashboard/library')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Library</span>
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {categoryFilter === 'favorites' && 'My Favorite Videos'}
            {categoryFilter === 'completed' && 'Completed Videos'}
            {categoryFilter === 'in-progress' && 'Videos In Progress'}
            {categoryFilter === 'all' && 'Video Library'}
          </h1>
          <p className="text-gray-600">
            {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''} • Last updated{' '}
            {new Date(videos[0]?.created_at || Date.now()).toLocaleDateString()}
          </p>
        </div>

        {continueWatchingVideos.length > 0 && categoryFilter === 'all' && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Continue Watching</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {continueWatchingVideos.map((video) => {
                const progress = continueWatching.find((p) => p.video_id === video.id);
                const progressPercent = (progress.progress_seconds / video.duration) * 100;

                return (
                  <div
                    key={video.id}
                    onClick={() => navigate(`/dashboard/videos/${video.id}`)}
                    className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300"
                  >
                    <div className="relative aspect-video bg-gray-100">
                      {video.thumbnail_url ? (
                        <img
                          src={video.thumbnail_url}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play size={48} className="text-gray-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <Play
                          size={48}
                          className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                        <div
                          className="h-full bg-blue-600 transition-all"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                        {video.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {Math.round(progressPercent)}% complete
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {featuredVideos.length > 0 && categoryFilter === 'all' && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Star className="text-yellow-500 fill-current" size={24} />
              Featured Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredVideos.map((video) => (
                <div
                  key={video.id}
                  onClick={() => navigate(`/dashboard/videos/${video.id}`)}
                  className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300"
                >
                  <div className="relative aspect-video bg-gray-100">
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play size={48} className="text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                      <Play
                        size={48}
                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                    <div className="absolute top-2 right-2">
                      <Star className="text-yellow-500 fill-current" size={20} />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {formatDuration(video.duration)}
                      </span>
                      <span>{video.view_count} views</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>

              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search videos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Tags</h3>
                <div className="space-y-2">
                  {allTags.map((tag) => {
                    const count = getTagCount(tag.id);
                    if (count === 0) return null;

                    return (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedTags.includes(tag.id)
                            ? 'ring-2 ring-offset-2'
                            : 'hover:bg-gray-50'
                        }`}
                        style={{
                          backgroundColor: selectedTags.includes(tag.id)
                            ? `${tag.color}20`
                            : 'transparent',
                          color: selectedTags.includes(tag.id) ? tag.color : '#374151',
                          ringColor: tag.color,
                        }}
                      >
                        <span>{tag.name}</span>
                        <span className="text-xs opacity-60">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {(selectedTags.length > 0 || searchQuery) && (
                <button
                  onClick={clearFilters}
                  className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <X size={16} />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {selectedTags.length > 0 || searchQuery
                  ? 'Filtered Results'
                  : categoryFilter === 'favorites'
                  ? 'My Favorites'
                  : categoryFilter === 'completed'
                  ? 'Completed'
                  : categoryFilter === 'in-progress'
                  ? 'In Progress'
                  : 'All Videos'}
              </h2>
              <p className="text-gray-600">{filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''}</p>
            </div>

            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredVideos.map((video) => (
                  <div
                    key={video.id}
                    onClick={() => navigate(`/dashboard/videos/${video.id}`)}
                    className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300"
                  >
                    <div className="relative aspect-video bg-gray-100">
                      {video.thumbnail_url ? (
                        <img
                          src={video.thumbnail_url}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play size={48} className="text-gray-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                        <Play
                          size={48}
                          className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        />
                      </div>
                      {favorites.has(video.id) && (
                        <div className="absolute top-2 right-2">
                          <Star className="text-yellow-500 fill-current" size={20} />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                        {video.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {video.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {formatDuration(video.duration)}
                        </span>
                        <span>{video.view_count} views</span>
                      </div>
                      {video.tags && video.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {video.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${tag.color}20`,
                                color: tag.color,
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
                <p className="text-gray-500 text-lg">No videos found</p>
                <p className="text-gray-400 mt-2">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
